<template>
  <div class="dashboard">

    <!-- Stats Cards -->
    <el-row :gutter="20" class="stats-row">
      <el-col :span="8">
        <el-card>
          <div class="stat-item">
            <div class="stat-label">账号总数</div>
            <div class="stat-value">{{ stats.total_accounts }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card>
          <div class="stat-item">
            <div class="stat-label">线索总数</div>
            <div class="stat-value">{{ stats.total_leads }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card>
          <div class="stat-item">
            <div class="stat-label">今日新增</div>
            <div class="stat-value">{{ stats.today_leads }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Task stats -->
    <el-row :gutter="20" class="stats-row">
      <el-col :span="8">
        <el-card>
          <div class="stat-item">
            <div class="stat-label">任务完成</div>
            <div class="stat-value">{{ stats.tasks_completed }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card>
          <div class="stat-item">
            <div class="stat-label">任务失败</div>
            <div class="stat-value" style="color:#f87171">{{ stats.tasks_failed }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card>
          <div class="stat-item">
            <div class="stat-label">任务成功率</div>
            <div class="stat-value" style="color:#22c55e">{{ successRate }}%</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 账号管理（独立区块） -->
    <el-card class="acct-card">
      <template #header>
        <div class="card-header">
          <span>👥 账号管理（{{ accounts.length }} 个）</span>
          <div class="card-header-actions">
            <el-button size="small" type="primary" plain @click="$router.push('/accounts')">＋ 添加账号</el-button>
            <el-button size="small" text @click="$router.push('/accounts')">全部管理 →</el-button>
          </div>
        </div>
      </template>
      <div v-if="accounts.length" class="acct-grid">
        <div v-for="a in accounts" :key="a.id" class="acct-tile" @click="$router.push('/accounts')">
          <div class="acct-avatar" :style="{ background: avatarGrad(a.id) }">{{ (a.nickname || a.username || '号')[0] }}</div>
          <div class="acct-info">
            <div class="acct-line1">
              <span class="acct-name">{{ a.nickname || a.username }}</span>
              <el-tag size="small" effect="plain" class="acct-plat">{{ platformName(a.platform) }}</el-tag>
            </div>
            <div class="acct-sub">{{ a.username || ('#' + a.id) }}<template v-if="a.industry"> · {{ a.industry }}</template></div>
          </div>
          <div class="acct-side">
            <span class="acct-dot" :class="{ on: a.is_logged_in }"></span>
            <span class="acct-state">{{ a.is_logged_in ? '已登录' : '未登录' }}</span>
          </div>
        </div>
      </div>
      <div v-else class="acct-empty">
        <div class="acct-empty-icon">🕳️</div>
        <div class="acct-empty-text">还没有绑定任何平台账号，采集 / 发布都需要账号登录态</div>
        <el-button type="primary" plain size="small" @click="$router.push('/accounts')">去添加账号</el-button>
      </div>
    </el-card>

    <!-- 双账本日报 -->
    <el-card>
      <template #header>
        <div class="card-header">
          <span>📒 双账本日报（采集账本 + 发送账本）</span>
          <el-radio-group v-model="ledgerDays" size="small" @change="loadLedger">
            <el-radio-button :label="3">近3天</el-radio-button>
            <el-radio-button :label="7">近7天</el-radio-button>
            <el-radio-button :label="14">近14天</el-radio-button>
          </el-radio-group>
        </div>
      </template>
      <el-table :data="ledgerRows" stripe style="width: 100%">
        <el-table-column prop="date" label="日期" width="110" />
        <el-table-column prop="account" label="账号" width="130" />
        <el-table-column label="📥 采集" align="center">
          <template #default="{ row }">
            <span>评论 <b style="color:#409EFF">{{ row.comments }}</b></span>
            <span style="margin-left:8px">线索 <b style="color:#67C23A">{{ row.leads }}</b></span>
            <span style="margin-left:8px">视频 <b style="color:#E6A23C">{{ row.videos }}</b></span>
          </template>
        </el-table-column>
        <el-table-column label="📤 发送" align="center">
          <template #default="{ row }">
            <span>私信 <b style="color:#F56C6C">{{ row.dm }}</b></span>
            <span style="margin-left:8px">留痕 <b style="color:#909399">{{ row.trace }}</b></span>
          </template>
        </el-table-column>
        <el-table-column prop="reached" label="👥 触达用户数" align="center" width="120">
          <template #default="{ row }">
            <b style="color:#409EFF">{{ row.reached }}</b>
          </template>
        </el-table-column>
        <el-table-column label="合计" align="center" width="90">
          <template #default="{ row }">
            <b>{{ row.comments + row.leads + row.reached }}</b>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!ledgerRows.length" style="text-align:center; color:#909399; padding:20px">
        暂无数据
      </div>
    </el-card>

    <!-- 发送账本明细 -->
    <el-card style="margin-top:20px">
      <template #header>
        <div class="card-header">
          <span>📤 发送账本明细（留痕 / 私信 / 关注同行）</span>
          <el-radio-group v-model="sendDays" size="small" @change="loadSendLogs">
            <el-radio-button :label="1">今天</el-radio-button>
            <el-radio-button :label="3">近3天</el-radio-button>
            <el-radio-button :label="7">近7天</el-radio-button>
          </el-radio-group>
        </div>
      </template>
      <el-table :data="sendLogs" stripe size="small" style="width: 100%">
        <el-table-column prop="created_at" label="时间" width="165" />
        <el-table-column prop="account" label="账号" width="140" />
        <el-table-column prop="action_label" label="动作" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.action === 'dm' ? 'danger' : (row.action === 'follow' ? 'warning' : 'info')" size="small">
              {{ row.action_label }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="target_username" label="目标用户" width="160" show-overflow-tooltip />
        <el-table-column prop="content" label="内容" min-width="220" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="90" align="center">
          <template #default="{ row }">
            <span :style="{ color: row.status === 'sent' ? '#67C23A' : '#F56C6C' }">
              {{ row.status === 'sent' ? '✅ 成功' : '❌ 失败' }}
            </span>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!sendLogs.length" style="text-align:center; color:#909399; padding:20px">
        暂无发送记录
      </div>
    </el-card>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'Dashboard',
  data() {
    return {
      stats: {
        total_accounts: 0,
        active_accounts: 0,
        total_leads: 0,
        today_leads: 0,
        tasks_completed: 0,
        tasks_failed: 0
      },
      ledgerDays: 7,
      ledgerRows: [],
      sendDays: 1,
      sendLogs: [],
      accounts: []
    }
  },
  computed: {
    successRate() {
      const t = this.stats.tasks_completed + this.stats.tasks_failed
      if (!t) return 100
      return Math.round((this.stats.tasks_completed / t) * 1000) / 10
    }
  },
  created() {
    this.loadStats()
    this.loadLedger()
    this.loadSendLogs()
    this.loadAccounts()
  },
  methods: {
    platformName(p) {
      return { douyin: '抖音', kuaishou: '快手', xhs: '小红书' }[p] || p || '平台'
    },
    avatarGrad(id) {
      const grads = [
        'linear-gradient(135deg,#2563EB,#38bdf8)',
        'linear-gradient(135deg,#7c3aed,#a78bfa)',
        'linear-gradient(135deg,#059669,#34d399)',
        'linear-gradient(135deg,#ea580c,#fb923c)',
        'linear-gradient(135deg,#db2777,#f472b6)'
      ]
      return grads[(id || 1) % grads.length]
    },
    async loadAccounts() {
      try {
        const r = await api.get('/accounts')
        this.accounts = Array.isArray(r) ? r : (r.accounts || [])
      } catch (e) {
        /* 账号加载失败不阻塞仪表盘 */
      }
    },
    async loadStats() {
      try {
        this.stats = await api.get('/analytics/dashboard')
      } catch (e) {
        this.$message.error('加载统计数据失败: ' + e.message)
      }
    },
    async loadLedger() {
      try {
        const r = await api.get('/analytics/ledger?days=' + this.ledgerDays)
        const rows = []
        for (const day of r.days || []) {
          for (const a of day.accounts || []) {
            if (!a.collect.comments && !a.collect.leads && !a.send.total && !a.reached_users) continue
            rows.push({
              date: day.date,
              account: a.account,
              comments: a.collect.comments,
              leads: a.collect.leads,
              videos: a.collect.videos,
              dm: (a.send.by_action && a.send.by_action.dm) || 0,
              trace: (a.send.by_action && a.send.by_action.trace) || 0,
              reached: a.reached_users
            })
          }
        }
        this.ledgerRows = rows
      } catch (e) {
        this.$message.error('加载双账本失败: ' + e.message)
      }
    },
    async loadSendLogs() {
      try {
        const r = await api.get('/analytics/send-logs?days=' + this.sendDays + '&limit=100')
        this.sendLogs = (r.items || []).filter(i => i.status === 'sent' || i.status === 'failed')
      } catch (e) {
        this.$message.error('加载发送账本明细失败: ' + e.message)
      }
    }
  }
}
</script>

<style scoped>
.dashboard {
  padding: 20px;
}

.stats-row {
  margin-bottom: 20px;
}

.stat-item {
  text-align: center;
  padding: 20px;
}

.stat-label {
  color: #e2e8f0;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 32px;
  font-weight: 800;
  color: #38bdf8;
}

.acct-card { margin-bottom: 20px; }

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.card-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.acct-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.acct-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(148, 163, 184, .15);
  border-radius: 12px;
  background: rgba(148, 163, 184, .04);
  cursor: pointer;
  transition: all .2s;
}
.acct-tile:hover {
  border-color: rgba(56, 189, 248, .5);
  background: rgba(37, 99, 235, .08);
  transform: translateY(-1px);
}

.acct-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.acct-info { flex: 1; min-width: 0; }

.acct-line1 {
  display: flex;
  align-items: center;
  gap: 6px;
}

.acct-name {
  font-size: 14px;
  font-weight: 700;
  color: #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 130px;
}

.acct-plat { flex-shrink: 0; }

.acct-sub {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.acct-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  flex-shrink: 0;
}

.acct-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #94a3b8;
}
.acct-dot.on { background: #22c55e; box-shadow: 0 0 6px rgba(34, 197, 94, .8); }

.acct-state {
  font-size: 11px;
  color: #94a3b8;
}
.acct-tile .acct-dot.on + .acct-state { color: #22c55e; }

.acct-empty {
  text-align: center;
  padding: 26px 10px;
  color: #94a3b8;
}
.acct-empty-icon { font-size: 30px; margin-bottom: 8px; }
.acct-empty-text { margin-bottom: 14px; font-size: 13px; }

@media (max-width: 767px) {
  .acct-grid { grid-template-columns: 1fr; }
  .card-header-actions .el-button + .el-button { display: none; } /* 手机端只留添加 */
}
</style>
